import { Link } from "react-router-dom";
import Seo from "./Seo";
import { organizationJsonLd } from "./business";
import { ArrowRight, WhatsappLogo, Calendar, Sparkle, ChatText, CheckCircle, Users, ClockCounterClockwise, ChartLineUp, HandHeart, ArrowsClockwise } from "@phosphor-icons/react";

const HIGHLIGHTS = [
  { icon: WhatsappLogo, title: "WhatsApp Reminders", desc: "Send timely service reminders via WhatsApp with one tap — powered by Meta's Cloud API." },
  { icon: Calendar, title: "Automatic Due-Date Tracking", desc: "Every customer's next-service date is auto-computed from the last visit and their service interval." },
  { icon: Sparkle, title: "AI Interval Suggestions", desc: "Pick a business type and let AI recommend the right service interval — no more guesswork." },
  { icon: ChatText, title: "Custom Message Templates", desc: "Design branded WhatsApp templates per business type with placeholders like customer name and next date." },
];

const BENEFITS = [
  { icon: Calendar, text: "Track customer service due dates" },
  { icon: Users, text: "Manage customer information" },
  { icon: WhatsappLogo, text: "Set reminders on WhatsApp" },
  { icon: ClockCounterClockwise, text: "Keep service history" },
  { icon: ArrowsClockwise, text: "Follow up with customers" },
  { icon: ChartLineUp, text: "Reduce missed service opportunities" },
  { icon: CheckCircle, text: "Save time" },
  { icon: HandHeart, text: "Improve customer relationships" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="DueMate — Never Miss a Customer Service Follow-Up"
        description="DueMate helps businesses keep track of customer service due dates, reminders and follow-ups — so customers know when their next service is due."
        jsonLd={organizationJsonLd()}
      />

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="anim-fade-up">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4361EE] mb-3">SaaS for service businesses</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
            Never Miss a Customer<br />Service Follow-Up
          </h1>
          <p className="text-base sm:text-lg text-[#4A4A4A] font-semibold mt-6 max-w-lg">
            DueMate helps businesses keep track of customer service due dates, reminders and follow-ups — so customers know when their next service is due.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/login" data-testid="hero-get-started" className="nb-btn bg-[#4361EE] text-white px-6 py-3 flex items-center gap-2">
              Get Started <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/contact" data-testid="hero-contact" className="nb-btn bg-white px-6 py-3">Contact Us</Link>
          </div>
        </div>
        <div className="nb-card p-6 bg-gradient-to-br from-[#4361EE] via-[#5B4FE9] to-[#06D6A0]">
          <img src="/icons/icon-512.png?v=4" alt="DueMate" className="w-full max-w-sm mx-auto rounded-md" />
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 border-t-2 border-black/10">
        <div className="mb-10">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">What you get</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-1">Everything you need to keep customers coming back.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map(({ icon: Icon, text }) => (
            <div key={text} className="nb-card p-5 flex items-start gap-3">
              <div className="w-10 h-10 bg-[#FFD166] border-2 border-black rounded-md flex items-center justify-center flex-shrink-0">
                <Icon size={20} weight="bold" />
              </div>
              <p className="font-bold text-sm leading-snug">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 border-t-2 border-black/10">
        <div className="mb-10">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">How it works</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-1">Simple. Automated. Focused on repeat business.</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="nb-card p-6">
              <div className="w-11 h-11 bg-[#4ECDC4] border-2 border-black rounded-md flex items-center justify-center mb-4">
                <Icon size={22} weight="bold" />
              </div>
              <h3 className="font-heading text-xl font-black mb-2">{title}</h3>
              <p className="text-sm text-[#4A4A4A] font-semibold">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 border-t-2 border-black/10">
        <div className="nb-card p-8 sm:p-10 bg-[#FFD166] flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">Start tracking your customers today.</h2>
            <p className="text-sm font-semibold text-[#111111] mt-2 max-w-xl">
              Set up your business profile in under 2 minutes.
            </p>
          </div>
          <Link to="/login" data-testid="cta-get-started" className="nb-btn bg-[#111111] text-white px-6 py-3 flex items-center gap-2 flex-shrink-0">
            Get Started <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </>
  );
}
